import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';

// Firebase imports
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  deleteToken,
  Messaging,
  isSupported
} from 'firebase/messaging';

@Injectable({
  providedIn: 'root'
})
export class FirebaseMessagingService {
  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;
  private currentToken: string | null = null;
  private isInitialized = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

  /**
   * Strip HTML tags so raw markup never shows in the OS notification popup
   */
  private stripHtml(value: string | undefined | null): string {
    return typeof value === 'string' ? value.replace(/<[^>]*>/g, '').trim() : '';
  }

  /**
   * Initialize Firebase Messaging
   */
  async initialize(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('Firebase Messaging: Not in browser environment');
      return false;
    }

    if (this.isInitialized) {
      console.log('Firebase Messaging: Already initialized');
      return true;
    }

    try {
      console.log('Firebase Messaging: Initializing...');

      // 1. Check if Firebase Messaging is supported
      const messagingSupported = await isSupported();
      if (!messagingSupported) {
        console.warn('Firebase Messaging: Not supported in this browser');
        return false;
      }

      // 2. Initialize Firebase App
      this.app = initializeApp(environment.firebase);
      console.log('Firebase Messaging: App initialized');

      // 3. Initialize Firebase Messaging
      this.messaging = getMessaging(this.app);
      console.log('Firebase Messaging: Messaging initialized');

      // 4. Set up message listener for foreground notifications
      this.setupMessageListener();

      this.isInitialized = true;
      return true;

    } catch (error) {
      console.error('Firebase Messaging: Initialization failed', error);
      return false;
    }
  }

  /**
   * Set up listener for incoming messages
   */
  // private setupMessageListener(): void {
  //   if (!this.messaging) return;

  //   // Listen for messages when app is in foreground
  //   onMessage(this.messaging, (payload) => {
  //     console.log('Firebase Messaging: Message received in foreground', payload);
  //     this.displayNotification(payload);
  //   });
  // }

  private setupMessageListener(): void {
  if (!this.messaging) return;

  onMessage(this.messaging, async (payload) => {
    console.log('Foreground message received:', payload);

    if (Notification.permission !== 'granted') return;

    const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');

    if (!registration) {
      console.warn('Service worker not registered');
      return;
    }

    const title =
      this.stripHtml(payload.notification?.title) ||
      this.stripHtml(payload.data?.title) ||
      'New Notification';

    const image = (payload.notification as any)?.imageUrl || payload.data?.imageUrl;

    const options: NotificationOptions = {
      body:
        this.stripHtml(payload.notification?.body) ||
        this.stripHtml(payload.data?.body) ||
        '',
      icon: '/assets/icons/192-192.png',
      badge: '/assets/icons/192-192.png',
      ...(image ? { image } : {}),
      data: payload.data || {}
    };

    await registration.showNotification(title, options);
  });
}


  /**
   * Display browser notification
   */
  private displayNotification(payload: any): void {
    if (!isPlatformBrowser(this.platformId) || Notification.permission !== 'granted') {
      return;
    }

    // Create notification options
    const title = this.stripHtml(payload.notification?.title) || 'New Notification';
    const options: NotificationOptions = {
      body: this.stripHtml(payload.notification?.body) || 'You have a new message',
      icon: payload.notification?.icon || '/assets/icons/192-192.png',
      badge: '/assets/icons/192-192.png',
      data: payload.data || {},
      requireInteraction: false
    };

    // Create and show notification
    const notification = new Notification(title, options);

    // Handle notification click
    notification.onclick = () => {
      notification.close();
      window.focus();

      // Handle URL from notification data
      if (payload.data?.url) {
        window.open(payload.data.url, '_blank');
      }
    };
  }

  /**
   * Request notification permission and get FCM token
   */
  async requestPermissionAndGetToken(): Promise<string | null> {
    if (!isPlatformBrowser(this.platformId) || !this.messaging) {
      console.warn('Firebase Messaging: Not available');
      return null;
    }

    try {
      console.log('Firebase Messaging: Requesting permission...');

      // 1. Request notification permission
      const permission = await Notification.requestPermission();
      console.log('Firebase Messaging: Permission status:', permission);

      if (permission !== 'granted') {
        console.warn('Firebase Messaging: Permission not granted');
        return null;
      }

      // 2. Get or register service worker
      let serviceWorkerRegistration: ServiceWorkerRegistration | undefined;

      if ('serviceWorker' in navigator) {
        try {
          // Try to get existing registration first
          const registration = await navigator.serviceWorker.getRegistration();

          if (registration) {
            serviceWorkerRegistration = registration;
            console.log('Firebase Messaging: Using existing service worker');
          } else {
            // Register new service worker
            serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            console.log('Firebase Messaging: Service worker registered');
          }
        } catch (swError) {
          console.warn('Firebase Messaging: Service worker registration failed:', swError);
          // Continue without custom service worker, Firebase will use default
        }
      }

      // 3. Get FCM token
      console.log('Firebase Messaging: Getting FCM token...');
      console.log('VAPID Key:', environment.vapidKey.substring(0, 20) + '...');
      console.log('=============  Firebase Messaging: FCM token obtained before============');

      const token = await getToken(this.messaging, {
        vapidKey: environment.vapidKey,
        serviceWorkerRegistration: serviceWorkerRegistration
      });
      console.log('=============  Firebase Messaging: FCM token obtained after============');
      if (token) {
        console.log('Firebase Messaging: Token obtained successfully');
        console.log('Token preview:', token.substring(0, 30) + '...');
        this.currentToken = token;

        // Store token in localStorage for persistence
        localStorage.setItem('fcmToken', token);

        return token;
      } else {
        console.warn('Firebase Messaging: No token available');
        return null;
      }

    } catch (error) {
      console.error('Firebase Messaging: Error getting token', error);

      // Detailed error handling
      if (error instanceof Error) {
        console.error('Error name:', error.name);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }

      return null;
    }
  }


  /**
   * Get current FCM token
   */
  getCurrentToken(): string | null {
    return this.currentToken || localStorage.getItem('fcmToken');
  }

  /**
   * Delete FCM token
   */
  async deleteToken(): Promise<boolean> {
    if (!this.messaging || !this.currentToken) {
      return false;
    }

    try {
      await deleteToken(this.messaging);
      this.currentToken = null;
      localStorage.removeItem('fcmToken');
      localStorage.removeItem('fcmTokenTimestamp');
      console.log('Firebase Messaging: Token deleted');
      return true;
    } catch (error) {
      console.error('Firebase Messaging: Error deleting token', error);
      return false;
    }
  }

  /**
   * Check if notifications are supported
   */
  async isSupported(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    return await isSupported();
  }

  /**
   * Check current permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!isPlatformBrowser(this.platformId) || !('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }
}